import { cva, type VariantProps } from "class-variance-authority";

export type VariantButtonProps = VariantProps<typeof tButton>;

const tButton = cva(
  "h-auto whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-[400ms] ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-900 focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        primary:
          // Stay in the same brand-blue family on hover (darken slightly,
          // keep white text). No shadow on hover — the bg darken is the
          // only feedback. Matches the restrained look the user signed
          // off on in /buttons-playground.
          "bg-primary-900 hover:bg-primary-800 text-white hover:text-white border-none",
        yellow:
          "bg-yellow-400 text-white hover:bg-yellow-500 hover:text-white border-none",
        outline:
          "border-[1px] border-white text-white bg-transparent hover:bg-black-900 hover:border-black-900",
        white:
          "bg-white text-black-900 hover:bg-black-900 hover:text-white border-none",
        black:
          "text-white bg-black-900 hover:text-black-900 hover:bg-white border-none",
        light_primary:
          "dark:border-primary-900/80 dark:bg-transparent dark:text-black-200 dark:hover:bg-primary-900 delay-400 linear bg-primary-100 px-4 py-1 text-sm font-normal text-black-700 transition duration-300 hover:bg-primary-900 hover:text-primary-100 md:px-5 md:py-[10px] md:text-base",
        orange:
          "bg-accent-500 hover:bg-accent-600 text-white hover:text-white border-none hover:shadow-[0_10px_25px_rgba(251,68,27,0.3)]",
        // Tertiary button — brand-blue border on transparent bg. Works on
        // both light and dark backgrounds. Use this when there's a third
        // CTA on a section: orange (primary) + primary (secondary) +
        // outline-brand (tertiary).
        //
        // NB: `outline` (above) is a different variant — a white border for
        // dark backgrounds — and it is a Strapi enum value stored against
        // live CMS content, so it keeps its name. This one was called
        // `outlined`, one letter apart, which made the two trivially
        // confusable at call sites.
        "outline-brand":
          "border-2 border-primary-900 text-primary-900 bg-transparent hover:bg-primary-900 hover:text-white",
        link: "text-[14px] leading-4",
      },
      size: {
        base: "h-11 w-fit px-7 py-2 lg:text-h7 text-base font-normal box-border",
        large:
          "h-14 w-fit px-10 py-3 lg:text-h5 text-lg font-medium box-border",
      },
      radius: {
        none: "rounded-none",
        full: "rounded-full",
      },
      disabled: {
        true: "cursor-not-allowed opacity-50",
        false: "cursor-pointer",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "base",
      radius: "none",
      disabled: false,
    },
  },
);

export default tButton;
